import React, { useMemo } from 'react';
import { WordCloud } from '@ant-design/plots';
import { FormattedMessage, useIntl } from 'react-intl';

// Та же палитра и тот же порядок, что у RatingDistribution: серый → голубой → сиреневый →
// красный → оранжевый. Цвет ниши читается одинаково в обоих местах страницы.
const QUALITY_COLORS = [
  { min: 400, color: '#FDB022' },   // $pc-warning-400
  { min: 300, color: '#F04438' },   // $pc-error-500
  { min: 200, color: '#C180F9' },   // $sc-violet-500
  { min: 100, color: '#87A8F2' },   // $pc-accent-500
  { min: 0,   color: '#D0D5DD' },   // $pc-gray-300
];

// Ниши, собранные из словаря, ещё никто не мерил — рейтинга у них нет, и красить их по шкале
// качества нечем. Даём почти чёрный ($pc-gray-900): в палитре качества такого цвета нет,
// поэтому «ещё не измеренные» видно в облаке сразу и они не путаются со слабыми (серыми).
const NEW_NICHE_COLOR = '#101828';

const DAY = 24 * 60 * 60 * 1000;

// Клик по слову ведёт на выдачу Adobe с УЖЕ выставленными фильтрами, которых требует парсер
// расширения (см. getFilterMismatch в KeyHunter/src/components/adobe/adobe.jsx): иначе на
// странице сразу висел бы тост «поправьте фильтры», а смысл подсказки — открыть и парсить.
// k и aco дублируют друг друга — так же, как в реальных URL, которые присылает расширение.
const ADOBE_SEARCH_URL = 'https://stock.adobe.com/search/images';
const ADOBE_DEFAULT_FILTERS = {
  'filters[content_type:photo]': '1',
  'filters[content_type:illustration]': '1',
  'filters[content_type:zip_vector]': '1',
  'filters[gentech]': 'exclude',
  order: 'relevance',
  'price[$]': '1',
  limit: '100',
  search_page: '1',
  search_type: 'usertyped',
};

const buildAdobeSearchUrl = (keyword) => {
  const url = new URL(ADOBE_SEARCH_URL);

  Object.entries(ADOBE_DEFAULT_FILTERS).forEach(([name, value]) => url.searchParams.set(name, value));
  url.searchParams.set('k', keyword);
  url.searchParams.set('aco', keyword);

  return url.toString();
};

const colorByScore = (score) => {
  const value = Number(score) || 0;

  return (QUALITY_COLORS.find((level) => value >= level.min) || QUALITY_COLORS[QUALITY_COLORS.length - 1]).color;
};

// Размер — по давности ПЕРЕСЧЁТА (updatedAt), а не по возрасту работ: облако предлагает, что
// пора отпарсить, и крупнее должно быть то, что дольше всех не обновляли.
const staleDays = (updatedAt) => {
  if (!updatedAt) return 1;

  return Math.max(1, Math.round((Date.now() - new Date(updatedAt).getTime()) / DAY));
};

const RatingSuggestions = ({ suggestions = [] }) => {
  const intl = useIntl();

  const data = useMemo(() => suggestions
    .filter((item) => item?.keyword)
    .map((item) => ({
      text: item.keyword,
      // Новая ниша, собранная из словаря, приходит без updatedAt — даём ей вес выше среднего,
      // чтобы она не потерялась мелким шрифтом среди давно не обновлявшихся.
      value: item.updatedAt ? staleDays(item.updatedAt) : 30,
      color: item.isNew ? NEW_NICHE_COLOR : colorByScore(item.score),
      // Дату форматируем здесь, а не в тултипе: там она нужна уже строкой.
      // Локаль ru-RU жёстко, как и в колонке Update таблицы, — формат даты один на весь рейтинг.
      updated: item.updatedAt
        ? new Date(item.updatedAt).toLocaleDateString('ru-RU')
        : intl.formatMessage({ id: 'rating.suggestions.never', defaultMessage: 'Not parsed yet' }),
    })), [suggestions, intl]);

  if (data.length === 0) return null;

  return (
    <div className="rating-suggestions">
      <div className="rating-suggestions__title">
        <FormattedMessage
          id="rating.suggestions.title"
          defaultMessage="Niches worth parsing — the bigger the word, the longer it hasn't been updated. Click to open the search"
        />
      </div>
      <WordCloud
        data={data}
        // rotate: 0 — все слова горизонтально. По умолчанию d3-cloud крутит их случайно
        // (`(~~(Math.random() * 6) - 3) * 30` градусов), а ниши здесь читают, а не разглядывают.
        layout={{ spiral: 'rectangular', fontSize: [12, 36], rotate: 0 }}
        textField="text"
        weightField="value"
        colorField="color"
        height={220}
        autoFit
        // identity — цвет берётся из данных как есть. Без него G2 назначил бы категориальную
        // палитру по значению поля, и наши hex'и превратились бы в произвольные цвета.
        scale={{ color: { type: 'identity' } }}
        legend={false}
        // title и items задаём ОБА: по умолчанию заголовком тултипа становится значение
        // цветового канала, то есть буквально «#87A8F2», а имя элемента — название поля («text»).
        tooltip={{
          title: (datum) => datum.text,
          items: [
            (datum) => ({
              name: intl.formatMessage({ id: 'rating.suggestions.updated', defaultMessage: 'Updated' }),
              value: datum.updated,
            }),
          ],
        }}
        onReady={(plot) => {
          // onReady отдаёт НЕ G2-чарт, а обёртку Plot из @ant-design/plots (типизация
          // `(chart: Chart) => void` в interface.d.ts врёт: в useChart.js передаётся
          // `new ChartClass(container, config)`). У неё G2-чарт лежит в .chart, а DOM — в
          // .container; своего getContainer() у обёртки нет.
          // События слушаем именно у plot.chart: Plot проксирует события G2 через
          // `emit(e.type, e)`, а `e.type` у клика по элементу — просто 'click', без префикса
          // 'element:', так что plot.on('element:click') не сработал бы никогда.
          const { chart, container } = plot;

          // off перед on: onReady вызывается на каждую готовность графика (в т.ч. после смены
          // данных), а без снятия старых обработчиков один клик открывал бы несколько вкладок.
          chart.off('element:click');
          chart.on('element:click', (event) => {
            const keyword = event?.data?.data?.text;

            if (keyword) window.open(buildAdobeSearchUrl(keyword), '_blank', 'noopener,noreferrer');
          });

          // Курсор переключаем на элементах, а не на всём блоке: облако рисуется в canvas,
          // и пустое место между словами не кликабельно.
          chart.off('element:pointerover');
          chart.on('element:pointerover', () => { container.style.cursor = 'pointer'; });
          chart.off('element:pointerout');
          chart.on('element:pointerout', () => { container.style.cursor = 'default'; });
        }}
      />
    </div>
  );
};

export default RatingSuggestions;
